import { Button, Empty, Input, Select, Table, Tag, message } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../../../../../nestserver";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_COLOURS = {
  approved: "green",
  pending: "orange",
  rejected: "red",
};

/**
 * Per-form consent roster: who has answered, what they said, and the emergency
 * / medical details they supplied for staff to carry on the day.
 */
const TripConsentStatus = ({ tripId, onTripChange }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState();
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(null);

  const trips = useQuery("/trip", async () => {
    const res = await axios.get(`${serverUrl}/trip`, { headers: authHeader() });
    return res.data;
  });

  const tripOptions = (trips?.data?.result?.data || []).map((trip) => ({
    label: `${trip.title} — ${moment(trip.from).format("DD MMM YYYY")}`,
    value: trip._id,
  }));

  // Land on the most recent trip so the tab is useful without a extra click.
  useEffect(() => {
    if (!tripId && tripOptions.length) {
      onTripChange(tripOptions[0].value);
    }
  }, [tripId, tripOptions, onTripChange]);

  const consents = useQuery(
    ["/trip/consents", tripId, status, search],
    async () => {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (search) params.append("search", search);
      const res = await axios.get(
        `${serverUrl}/trip/${tripId}/consents?${params.toString()}`,
        { headers: authHeader() },
      );
      return res.data;
    },
    { enabled: !!tripId },
  );

  const handleRemind = async () => {
    try {
      const res = await axios.post(
        `${serverUrl}/trip/${tripId}/remind`,
        {},
        { headers: authHeader() },
      );
      message.success(res?.data?.result?.message || "Reminder sent");
      queryClient.invalidateQueries("/trip/consents");
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    }
  };

  const downloadPdf = async (record) => {
    setDownloading(record._id);
    try {
      const res = await axios({
        url: `${serverUrl}/trip/consent/${record._id}/download`,
        method: "GET",
        responseType: "blob",
        headers: { Accept: "application/pdf", ...authHeader() },
      });
      const safeChild = String(record.student || "Student").replace(
        /[^a-zA-Z0-9]/g,
        ""
      );
      saveAs(res.data, `Consent_${safeChild}.pdf`);
    } catch (err) {
      message.error("Could not download the signed form");
    } finally {
      setDownloading(null);
    }
  };

  const columns = [
    {
      title: "Student",
      width: 180,
      render: (record) => record.student || "—",
    },
    { title: "Class", width: 120, render: (record) => record.class || "—" },
    {
      title: "Parent",
      width: 190,
      render: (record) => (
        <div>
          <div>{record.parent || "—"}</div>
          <div className="text-xs text-gray-500">{record.parent_phone}</div>
        </div>
      ),
    },
    {
      title: "Status",
      width: 110,
      render: (record) => (
        <Tag color={STATUS_COLOURS[record.status] || "default"}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Responded",
      width: 140,
      render: (record) =>
        record.responded_at
          ? moment(record.responded_at).format("DD-MM-YYYY hh:mm A")
          : "—",
    },
    {
      title: "Signed by",
      width: 160,
      render: (record) => record.signature_name || "—",
    },
    {
      title: "Emergency contact",
      width: 160,
      render: (record) => record.emergency_contact || "—",
    },
    {
      title: "Medical notes",
      width: 220,
      render: (record) => record.medical_notes || "—",
    },
    {
      title: "Remarks",
      width: 220,
      render: (record) => record.remarks || "—",
    },
    {
      title: "Signed form",
      width: 140,
      fixed: "right",
      render: (record) =>
        record.status === "approved" ? (
          <Button
            size="small"
            loading={downloading === record._id}
            onClick={() => downloadPdf(record)}
          >
            Download PDF
          </Button>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="pt-8">
      <div className="bg-white p-10 rounded-2xl">
        <div className="text-2xl text-orange-500 font-semibold pb-8">
          Consent Status
        </div>

        <div className="flex flex-wrap gap-4 pb-4">
          <Select
            showSearch
            placeholder="Select a consent form"
            style={{ width: 320 }}
            optionFilterProp="label"
            value={tripId}
            onChange={onTripChange}
            options={tripOptions}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={[
              { label: "Pending", value: "pending" },
              { label: "Approved", value: "approved" },
              { label: "Declined", value: "rejected" },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Search student"
            style={{ width: 240 }}
            onSearch={setSearch}
          />
          <Button disabled={!tripId} onClick={handleRemind}>
            Remind pending parents
          </Button>
        </div>

        {tripId ? (
          <Table
            rowKey="_id"
            className="rounded-2xl"
            loading={consents.isLoading}
            columns={columns}
            scroll={{ x: 1640 }}
            dataSource={consents?.data?.result?.data || []}
          />
        ) : (
          <Empty description="No consent forms yet" />
        )}
      </div>
    </div>
  );
};

export default TripConsentStatus;
